import { Injectable } from '@angular/core';
import { filter, BehaviorSubject, firstValueFrom,  Observable, map } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'environments/environment';
import Keycloak from 'keycloak-js';
import { UserProfileI } from '@wind-log_library/fe-component-library';
import { MapUserProfile } from './user-profile.models';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  private userData$ = new BehaviorSubject<UserProfileI | null>(null);
  private userProfileLoaded$ = new BehaviorSubject<boolean>(false);
  private userProfile: UserProfileI | null = null;
  private allUserRoles: string[] = [];
  private BACKEND_URL = environment.image_server + '/user';
  private token;

  private allowedPortfoliosRevenuesV2 = [
    '66f3de3391b5dfb62dc2d2a3', // Forestalia
    '688b48c7efc500f363079091', // Equinor
    '64930127d5cb2e4787965e0f', // HelenOY
    '5cdca835b50ef7724fabaefc', '2', 2, // Demo (This portfolio may have '5cdca835b50ef7724fabaefc', '2' or 2 as portfolio_id)
    '653fc10a48334fb975f87926', // Audax
    '5cafaa2dd266d532530bfabc', '1', 1, // BKW (This portfolio may have '5cafaa2dd266d532530bfabc', '1' or 1 as portfolio_id)
  ];

  constructor(
    private keycloakService: Keycloak,
    private http: HttpClient
  ) {}

  public loadUserProfile(): void {
    if (this.userProfileLoaded$.getValue()) return;

    this.keycloakService
      .loadUserProfile()
      .then((profile) => {
        this.allUserRoles = this.keycloakService.realmAccess?.roles || [];

        this.userProfile = MapUserProfile.mapData(profile);

        this.userProfile.appsAvailable = this.allUserRoles.filter((role) => role.startsWith('app_'));
        this.userProfile.modulesAvailable = this.allUserRoles.filter((role) => role.startsWith('module_') || role.startsWith('submodule_'));
        this.token = this.keycloakService.token;
        this.userData$.next(this.userProfile);
        this.userProfileLoaded$.next(true);
      })
      .catch((err) => {
        console.error('Error loading user profile from Keycloak', err);
      });
  }

  public hasRole(role: string): boolean {
    const effectiveRoles = this.allUserRoles.length ? this.allUserRoles : this.keycloakService.realmAccess?.roles || [];
    return effectiveRoles.includes(role);
  }

  public canAccessToApp(app: string): boolean {
    if (this.userProfile?.appsAvailable) {
      return this.userProfile.appsAvailable.includes(app);
    }
    const roles = this.keycloakService.realmAccess?.roles || [];
    return roles.some((role) => role.startsWith('app_') && role === app);
  }

  public canAccessToModule(module: string): boolean {
    if (this.userProfile?.modulesAvailable) {
      return this.userProfile.modulesAvailable.includes(module);
    }
    const roles = this.keycloakService.realmAccess?.roles || [];
    return roles.some((role) => (role.startsWith('module_') || role.startsWith('submodule_')) && role === module);
  }

  public canAccess(roles: string[]): boolean {
    if (!roles) return false;
    const effectiveRoles = this.allUserRoles.length ? this.allUserRoles : this.keycloakService.realmAccess?.roles || [];
    return roles.some((role) => role && effectiveRoles.includes(role));
  }

  public userProfileLoaded(): BehaviorSubject<boolean> {
    return this.userProfileLoaded$;
  }

  public getUserData(): Observable<UserProfileI> {
    return this.userData$.asObservable().pipe(filter((d): d is UserProfileI => !!d));
  }

  public getUserProfile(): any {
    return this.userProfile;
  }

  public updateLocalProfile(partialProfile: Partial<UserProfileI>): void {
    if (!this.userProfile) return;

    this.userProfile = { ...this.userProfile, ...partialProfile };

    this.userProfile.attributes = this.userProfile.attributes?.map((attr) =>
      (partialProfile as any)[attr.key] ? { ...attr, value: (partialProfile as any)[attr.key] } : attr
    );

    this.userData$.next(this.userProfile);
  }

  public getToken(): string | undefined {
    return this.token;
  }

  public uploadUserImage(userId: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('image', file);
    return this.http.post(this.BACKEND_URL, formData);
  }

  public isInternalUser(): boolean {
    const profile = this.getUserProfile();
		const isInternalUser = profile?.attributes?.find(a => a.key === 'internal_user')?.value?.[0] === 'true' || false;
    return isInternalUser;
  }

  public isPortfolioAdmin(): boolean {
    const profile = this.getUserProfile();
    const isPortfolioAdmin = profile?.attributes?.find(a => a.key === 'is_portfolio_admin')?.value?.[0] === 'true' || false;
    return isPortfolioAdmin;
  }

  public hasAccessToRevenuesV1(): boolean {
    return this.isInternalUser() || (!this.isInternalUser() && !this.hasAccessToRevenuesV2());
  }

  public hasAccessToRevenuesV2(): boolean {
    const profile = this.getUserProfile();
    const portfolioRights = profile?.attributes && profile?.attributes?.find(a => a.key === 'portfolio_rights')?.value?.[0] || '{}';
    const portfolioId = JSON.parse(portfolioRights)?.portfolio_id?.toString() || '';
    const hasAccessToRevenuesV2 = this.allowedPortfoliosRevenuesV2.includes(portfolioId);
    return this.isInternalUser() || (!this.isInternalUser() && hasAccessToRevenuesV2);
  }

  public async checkUserImageExists(image: string): Promise<boolean> {
    return firstValueFrom(
      this.http
        .get<{ ok: boolean; result: boolean }>(`${this.BACKEND_URL}/exists/${image}`)
        .pipe(map(response => response.result))
      );
  }

  public deleteUserImage(image: string): Observable<any>{
    return this.http.delete(`${this.BACKEND_URL}/${image}`)
  }

}
